import { beautifyNumber } from "utils/numberModification";
import { RawWoodSpeciesItem } from "../rawWoodInputInformationSpecies";
import { LogEntry } from "../rawWoodInputMeasurementLogs";
import { UserOrg } from "types/auth";
import dayjs from "dayjs";

export type DiameterRange = {
  min: number;
  max: number;
  label: string;
};

export const getSellerData = (
  userOrg: UserOrg | null,
  translate: (key: string) => string
): string => {
  if (!userOrg) return "";
  return `${userOrg.name || ""}\n${translate("stack-delivery-note-cvr")}: ${
    userOrg.businessRegNo || "-"
  }\n${userOrg.address || ""}\n${userOrg.zipcode || ""} ${userOrg.city || ""}`;
};

export const getBuyerData = (
  selectedBuyerOrgData: UserOrg | null,
  translate: (key: string) => string
): string => {
  if (!selectedBuyerOrgData) return "";
  return `${selectedBuyerOrgData.name || ""}\n${translate("stack-delivery-note-cvr")}: ${
    selectedBuyerOrgData.businessRegNo || "-"
  }\n${selectedBuyerOrgData.address || ""}\n${selectedBuyerOrgData.zipcode || ""} ${
    selectedBuyerOrgData.city || ""
  }`;
};

export const getSpeciesArrayText = (
  variant: string,
  initSpeciesArray: RawWoodSpeciesItem[] | null,
  woodChipSpecies: string | null,
  initLogRows: LogEntry[] | null,
  getSpeciesNameAndAbbreviation: (
    id: string,
    type?: string
  ) => { abbreviation: string; name: string },
  translate: (key: string) => string
): { abr: string; lat: string }[] => {
  let speciesArrText: { abr: string; lat: string }[] = [];

  if (["WoodStack", "WoodChipStack"].includes(variant) && initSpeciesArray && initSpeciesArray.length > 0) {
    speciesArrText = initSpeciesArray.map((spc) => {
      const abr = getSpeciesNameAndAbbreviation(spc.id).abbreviation;
      const latinName = getSpeciesNameAndAbbreviation(spc.id, "la").name;
      return { abr, lat: `(${latinName})` };
    });
  } else if (variant === "WoodLogStack" && initLogRows && initLogRows.length > 0) {
    let insertedSpc: string[] = [];
    initLogRows.forEach((log) => {
      const abr = getSpeciesNameAndAbbreviation(log.species).abbreviation;
      const latinName = getSpeciesNameAndAbbreviation(log.species, "la").name;
      if (!insertedSpc.includes(log.species)) {
        insertedSpc.push(log.species);
        speciesArrText.push({ abr, lat: `(${latinName})` });
      }
    });
  }

  return speciesArrText;
};

export const processLogData = (data: LogEntry[], diameterRanges: DiameterRange[]) => {
  if (!data || data.length === 0) return undefined;
  const totalLogs: number[] = new Array(diameterRanges.length).fill(0);
  const totalVol: number[] = new Array(diameterRanges.length).fill(0);
  const avgLength: number[] = new Array(diameterRanges.length).fill(0);
  const avgVolPerLog: number[] = new Array(diameterRanges.length).fill(0);
  let totalLengthSum: number = 0;
  let totalVolSum: number = 0;
  let totalLogsCount: number = data.length;

  data.forEach((item) => {
    const diameter = parseFloat(item.diameter.replace(",", "."));
    const length = parseFloat(item.length.replace(",", "."));
    const volume = parseFloat(item.volume.replace(",", "."));

    totalLengthSum += length;
    totalVolSum += volume;

    // Determine which diameter pool the log belongs to
    let diameterIndex = -1;
    for (let i = 0; i < diameterRanges.length; i++) {
      const range = diameterRanges[i];
      if (diameter >= range.min && diameter <= range.max) {
        diameterIndex = i;
        break;
      }
    }

    if (diameterIndex !== -1) {
      // Update totals for the corresponding diameter pool
      totalLogs[diameterIndex]++;
      totalVol[diameterIndex] += volume;
      avgLength[diameterIndex] += length;
      avgVolPerLog[diameterIndex] += volume;
    }
  });

  // Calculate averages for avgLength and avgVolPerLog
  avgLength.forEach((_, index) => {
    if (totalLogs[index] > 0) {
      avgLength[index] /= totalLogs[index];
      avgVolPerLog[index] /= totalLogs[index];
    }
  });
  const totalAvgLength = totalLengthSum / totalLogsCount;
  const totalAvgVolPerLog = totalVolSum / totalLogsCount;

  return {
    totalLogs,
    totalVol,
    avgLength,
    avgVolPerLog,
    totalLogsCount,
    totalAvgLength,
    totalAvgVolPerLog,
    totalVolSum,
  };
};

// TODO: Theses function are not fully implemented yet
export const createBaseDataObject = (
  location: { lat: number; lng: number },
  seller: string,
  buyer: string,
  measResponsible: string,
  forestName: string,
  certificationNumber: string,
  inventoryStatusNote: string,
  internalAppVersion: string,
  translate: (key: string) => string,
) => ({
  headerLabels: {
    title: translate("delivery-note-title"),
    date: translate("stack-delivery-note-date"),
    buyer: translate("stack-delivery-note-buyer"),
    seller: translate("stack-delivery-note-seller"),
    measuredBy: translate("stack-delivery-note-measured-by"),
    forest: translate("stack-delivery-note-forest"),
    certificate: translate("stack-delivery-note-certificate"),
    note: translate("stack-delivery-note-note"),
  },
  headerData: {
    date: `${dayjs().format("DD.MM.YYYY")}`,
    seller: seller,
    buyer: buyer,
    measuredBy: measResponsible,
    forest: forestName,
    certificateNumber: certificationNumber ? certificationNumber : "-",
    note: inventoryStatusNote || "-",
  },

  overviewLabels: {
    header: translate("stack-delivery-note-stack-data"),
    stackNumber: translate("stack-delivery-note-stack-number"),
    assortment: translate("stack-delivery-note-assortment"),
    species: translate("stack-delivery-note-species"),
    effectiveLength: translate("stack-delivery-note-effective-length"),
    avgDiameter: translate("stack-delivery-note-avg-diameter"),
    volumeRm: translate("stack-delivery-note-volume-rm"),
    volumeM3: translate("stack-delivery-note-volume-m3"),
    weightTon: translate("stack-delivery-note-weight-ton"),
    map: translate("stack-delivery-note-map"),
    gps: translate("stack-delivery-note-gps"),
    origin: translate("stack-delivery-note-origin"),
    link: {
      label: translate("stack-delivery-note-map-link"),
      href: `https://www.google.com/maps?q=${location.lat},${location.lng}`,
    },
  },

  measurementsLabels: {
    header: translate("stack-delivery-note-measurements-and-observations"),
    section: translate("stack-delivery-note-section"),
    front: translate("stack-delivery-note-front"),
    back: translate("stack-delivery-note-back"),
    correction: translate("stack-delivery-note-correction"),
    middleValues: translate("stack-delivery-note-middle-values"),
    height: translate("stack-delivery-note-stack-height"),
    length: translate("stack-delivery-note-stack-length"),
    solidPercentage: translate("stack-delivery-note-solid-percentage"),
    supportCount: translate("stack-delivery-note-support-count"),
    rejectCount: translate("stack-delivery-note-reject-count"),
  },

  metricsLabels: {
    volumeAndWeight: translate("stack-delivery-note-volume-and-weight"),
    volumeRm: translate("stack-delivery-note-volume-rm"),
    volumeM3: translate("stack-delivery-note-volume-m3"),
    weightTon: translate("stack-delivery-note-weight-ton"),
    bark: false
      ? translate("stack-delivery-note-under-bark")
      : translate("stack-delivery-note-over-bark"),
  },

  stackDeliveryNoteFooter: translate(`print-footer-${internalAppVersion}`),
});

export const createOverviewData = (
  uniqueInventoryId: string,
  assortmentStr: string,
  speciesArrText: any,
  logLengthStr: string,
  diameterStr: string,
  averageStackHeightMNum: number,
  stackLengthStr: string,
  result: any,
  location: any,
  originsStr: string,
  translate: (key: string) => string,
) => ({
  stackNumber: uniqueInventoryId,
  assortment: assortmentStr,
  species: speciesArrText,
  effectiveLength: logLengthStr,
  avgDiameter: diameterStr,
  avgStackHeight: `${beautifyNumber(averageStackHeightMNum, 2)} m`,
  avgStackLength: stackLengthStr,
  volumeRm: `${beautifyNumber(result.stackedVolumeM3, 2)} ${translate("unit-rm")}`,
  volumeM3: `${beautifyNumber(result.volumeM3, 2)} m3`,
  weightTon: `${beautifyNumber(result.greenMassTon, 2)} ${translate("unit-ton")}`,
  gpsCoordinates: { lat: location.lat, long: location.lng },
  origin: originsStr,
});

export const createMeasurementsData = (
  measArr: any,
  result: any,
  battensValue: any,
  rejectsValue: any,
  speciesPercentage: number | undefined,
  logDiameterPercentage: number | undefined,
  stackingPercentage: number | undefined,
  straightnessPercentage: number | undefined,
  processingPercentage: number | undefined,
  snowIcePercentage: number | undefined,
  residuesPercentage: number | undefined,
  stackHeightPercentage: number | undefined,
  logLengthPercentage: number | undefined,
  averageStackHeightMNum: number,
  stackLengthStr: string,
  translate: (key: string) => string,
) => ({
  measurements: measArr,
  solidInputLabels: {
    base: translate("input-dialog-raw-wood-input-conversion-factor-base"),
    meanDiameter: translate("input-dialog-raw-wood-input-conversion-factor-mean-diameter"),
    stacking: translate("input-dialog-raw-wood-input-conversion-factor-stacking"),
    straightness: translate("input-dialog-raw-wood-input-conversion-factor-straightness"),
    processing: translate("input-dialog-raw-wood-input-conversion-factor-processing"),
    snowAndIce: translate("input-dialog-raw-wood-input-conversion-factor-snow-ice"),
    residues: translate("input-dialog-raw-wood-input-conversion-factor-residues"),
    stackHeight: translate("input-dialog-raw-wood-input-conversion-factor-stack-height"),
    logLength: translate("input-dialog-raw-wood-input-conversion-factor-log-length"),
  },
  solidInputs: {
    base: speciesPercentage !== undefined ? `${beautifyNumber(speciesPercentage, 0)} %` : "-",
    meanDiameter:
      logDiameterPercentage !== undefined ? `${beautifyNumber(logDiameterPercentage, 0)} %` : "-",
    stacking: stackingPercentage !== undefined ? `${beautifyNumber(stackingPercentage, 0)} %` : "-",
    straightness:
      straightnessPercentage !== undefined ? `${beautifyNumber(straightnessPercentage, 0)} %` : "-",
    processing:
      processingPercentage !== undefined ? `${beautifyNumber(processingPercentage, 0)} %` : "-",
    snowAndIce: snowIcePercentage !== undefined ? `${beautifyNumber(snowIcePercentage, 0)} %` : "-",
    residues: residuesPercentage !== undefined ? `${beautifyNumber(residuesPercentage, 0)} %` : "-",
    stackHeight:
      stackHeightPercentage !== undefined ? `${beautifyNumber(stackHeightPercentage, 0)} %` : "-",
    logLength:
      logLengthPercentage !== undefined ? `${beautifyNumber(logLengthPercentage, 0)} %` : "-",
  },
  avgHeight: `${beautifyNumber(averageStackHeightMNum, 2)} m`,
  avgLength: stackLengthStr,
  solidPercentage: `${beautifyNumber(result.compensationFactorPercentage, 0)} %`,
  countSupports: `${battensValue} ${translate("stack-delivery-note-pcs")} (${beautifyNumber(
    result.supportVol,
    2
  )} m3)`,
  countRejects: `${rejectsValue} ${translate("stack-delivery-note-pcs")} (${beautifyNumber(
    result.rejectsVol,
    2
  )} m3)`,
});

export const createMetricsData = (result: any, translate: (key: string) => string,) => ({
  volumeRm: `${beautifyNumber(result.stackedVolumeM3, 1)} ${translate("unit-rm")}`,
  volumeM3: `${beautifyNumber(result.volumeM3, 1)} m3`,
  weightTon: `${beautifyNumber(result.greenMassTon, 1)} ${translate("unit-ton")}`,
});
